import type {
  Answer,
  FieldDefinition,
  ValidationIssue,
} from '@inside-us-aos/domain';

import { resolveVisibleFieldIds } from '../engine/visibility';

/**
 * Repeatable-group state + bound enforcement.
 *
 * Each catalog field tagged with a `repeatableGroupId` shares a
 * `repeatableBound` (per the Phase 0.5 invariant). This module
 * resolves group instances from the answer set, exposes add/remove
 * affordances honoring the bound, and emits issues / overflow
 * descriptors when a group is below `min` or above `max`.
 *
 * "Instance" semantics: an answer's `groupInstanceId` is opaque to the
 * engine; it is supplied by callers (Slice 7's state container will
 * own assignment). We treat the set of distinct `groupInstanceId`
 * values observed across all answers for fields in the group as the
 * instance roster. Fields outside the group are ignored.
 *
 * Per-form caps (e.g., I-130 has 5 address-history slots, I-485 has
 * 3) are NOT enforced here. The catalog `repeatableBound.max` is the
 * lowest-common (intake) cap; per-form fan-out is the PDF engine's
 * job in Phase 4.
 */

export type RepeatableOverflowStrategy =
  NonNullable<FieldDefinition['repeatableBound']>['overflowStrategy'];

export interface RepeatableBoundResolved {
  min: number;
  max: number;
  overflowStrategy: RepeatableOverflowStrategy;
}

export interface RepeatableGroupInstance {
  /** Caller-supplied instance id (or `undefined` for un-tagged answers). */
  instanceId: string | undefined;
  /** Stable index in encounter order (0-based). */
  index: number;
}

export interface RepeatableGroupState {
  groupId: string;
  bound: RepeatableBoundResolved;
  instances: readonly RepeatableGroupInstance[];
  canAddInstance: boolean;
  /**
   * Returns true when removing the instance at `index` keeps the
   * group at or above `min`. `instanceId` is accepted for symmetry
   * with caller-side flows that hold an id rather than an index.
   */
  canRemoveInstance: (index: number) => boolean;
  /**
   * Set when `instances.length > bound.max`. Carries the catalog's
   * declared overflow strategy. Null when the group is within bounds.
   */
  overflow: RepeatableOverflowStrategy | null;
}

const resolveBound = (
  bound: NonNullable<FieldDefinition['repeatableBound']>,
): RepeatableBoundResolved => ({
  min: bound.min ?? 0,
  max: bound.max,
  overflowStrategy: bound.overflowStrategy,
});

interface GroupCatalogEntry {
  fieldIds: Set<string>;
  bound: RepeatableBoundResolved;
}

/**
 * Indexes the catalog by `repeatableGroupId`. The Phase 0.5 invariant
 * guarantees every member shares the same `repeatableBound`; we read
 * it from the first member encountered. Throws if a member is tagged
 * with `repeatableGroupId` but missing `repeatableBound`, since that
 * combination is malformed (the catalog tests in @inside-us-aos/domain
 * already enforce this, but we defend at runtime in case a caller
 * passes a hand-rolled subset).
 */
const indexCatalogGroups = (
  catalog: readonly FieldDefinition[],
): Map<string, GroupCatalogEntry> => {
  const groups = new Map<string, GroupCatalogEntry>();
  for (const field of catalog) {
    if (!field.repeatableGroupId) continue;
    if (!field.repeatableBound) {
      throw new Error(
        `Field ${field.id} has repeatableGroupId without repeatableBound`,
      );
    }
    const existing = groups.get(field.repeatableGroupId);
    if (existing) {
      existing.fieldIds.add(field.id);
    } else {
      groups.set(field.repeatableGroupId, {
        fieldIds: new Set([field.id]),
        bound: resolveBound(field.repeatableBound),
      });
    }
  }
  return groups;
};

const collectInstancesForGroup = (
  fieldIds: ReadonlySet<string>,
  answers: readonly Answer[],
): RepeatableGroupInstance[] => {
  const seen = new Map<string | undefined, number>();
  const instances: RepeatableGroupInstance[] = [];
  for (const answer of answers) {
    if (!fieldIds.has(answer.fieldId)) continue;
    const id = answer.groupInstanceId;
    if (seen.has(id)) continue;
    seen.set(id, instances.length);
    instances.push({ instanceId: id, index: instances.length });
  }
  return instances;
};

const buildState = (
  groupId: string,
  bound: RepeatableBoundResolved,
  instances: readonly RepeatableGroupInstance[],
): RepeatableGroupState => ({
  groupId,
  bound,
  instances,
  canAddInstance: instances.length < bound.max,
  canRemoveInstance: (index: number) =>
    index >= 0 && index < instances.length && instances.length > bound.min,
  overflow: instances.length > bound.max ? bound.overflowStrategy : null,
});

/**
 * Resolves the state of a single repeatable group given the current
 * answers. Returns `null` when `groupId` is not a known catalog group.
 */
export const resolveRepeatableGroupState = (
  catalog: readonly FieldDefinition[],
  groupId: string,
  answers: readonly Answer[] = [],
): RepeatableGroupState | null => {
  const groups = indexCatalogGroups(catalog);
  const entry = groups.get(groupId);
  if (!entry) return null;
  const instances = collectInstancesForGroup(entry.fieldIds, answers);
  return buildState(groupId, entry.bound, instances);
};

/**
 * Resolves every catalog repeatable group's state in one pass.
 * Useful for engine-level recomputes (Slice 7) that need the full
 * picture per change event.
 */
export const resolveAllRepeatableGroupStates = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ReadonlyMap<string, RepeatableGroupState> => {
  const groups = indexCatalogGroups(catalog);
  const result = new Map<string, RepeatableGroupState>();
  for (const [groupId, entry] of groups) {
    const instances = collectInstancesForGroup(entry.fieldIds, answers);
    result.set(groupId, buildState(groupId, entry.bound, instances));
  }
  return result;
};

export interface RepeatableBoundIssue extends ValidationIssue {
  /**
   * Group identifier that triggered the issue. Mirrors the
   * `oneOfGroupId` discriminator on `ValidationIssue` for one-of
   * groups, but lives in a Phase 1 form-engine extension because the
   * domain `ValidationIssue` does not yet model group bounds.
   */
  repeatableGroupId: string;
  /** Strategy declared on the catalog bound (relevant for overflow). */
  overflowStrategy?: RepeatableOverflowStrategy;
}

/**
 * Selects the anchor field id for a group's bound issue. Mirrors the
 * domain `validateOneOfGroups` convention (P1 fix from Phase 1
 * review): anchor on the alphabetically-first VISIBLE member id.
 * If no member is visible (the group is gated by an unmet visibility
 * rule), fall back to the alphabetically-first declared member —
 * the bound issue still needs a stable fieldId to satisfy the
 * `ValidationIssue` contract, even if no UI will render it.
 */
const pickAnchorFieldId = (
  fieldIds: ReadonlySet<string>,
  visibleFieldIds: ReadonlySet<string>,
): string => {
  const sorted = [...fieldIds].sort();
  const firstVisible = sorted.find(id => visibleFieldIds.has(id));
  // Fallback: no visible member. Use alphabetically-first declared
  // member so the anchor remains stable across catalog reorderings.
  return firstVisible ?? sorted[0]!;
};

/**
 * Returns issues for any group that violates its bound:
 *   - Below `min` → `value_out_of_range` issue (one per group).
 *   - Above `max` AND `overflowStrategy === 'reject'` →
 *     `value_out_of_range` issue (the catalog explicitly says the
 *     count is hard-bounded; e.g., sponsor.tax_history requires
 *     exactly 3 years).
 * Other overflow strategies (`additional_info`, `truncate`) are NOT
 * issues at the engine layer — they carry overflow descriptors only.
 * The state object's `overflow` field surfaces them for the UI / PDF
 * engine to act on.
 *
 * Issues are anchored on the alphabetically-first **visible** member
 * field id, matching the domain `validateOneOfGroups` convention
 * (Phase 1.1 fix from the review). When a group has zero visible
 * members the anchor falls back to the alphabetically-first
 * declared member — the issue still needs a stable fieldId.
 */
export const enforceRepeatableBounds = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): readonly RepeatableBoundIssue[] => {
  const issues: RepeatableBoundIssue[] = [];
  const groups = indexCatalogGroups(catalog);
  const visibleFieldIds = resolveVisibleFieldIds(catalog, answers);
  for (const [groupId, entry] of groups) {
    const instances = collectInstancesForGroup(entry.fieldIds, answers);
    const count = instances.length;
    const anchor = pickAnchorFieldId(entry.fieldIds, visibleFieldIds);
    if (count < entry.bound.min) {
      issues.push({
        fieldId: anchor as ValidationIssue['fieldId'],
        code: 'value_out_of_range',
        message: `Group ${groupId} requires at least ${entry.bound.min} entries (have ${count}).`,
        repeatableGroupId: groupId,
      });
      continue;
    }
    if (count > entry.bound.max && entry.bound.overflowStrategy === 'reject') {
      issues.push({
        fieldId: anchor as ValidationIssue['fieldId'],
        code: 'value_out_of_range',
        message: `Group ${groupId} accepts at most ${entry.bound.max} entries (have ${count}).`,
        repeatableGroupId: groupId,
        overflowStrategy: 'reject',
      });
    }
  }
  return issues;
};
